import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DollarSign, TrendingUp, Users, Target, Building2, 
  Briefcase, Clock, AlertTriangle, Save, Loader2, CheckCircle2,
  Compass, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const Business = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    industry: '',
    company_name: '',
    company_stage: '',
    role: '',
    decision_style: '',
    goals_q1: '',
    goals_annual: ''
  });
  const [business, setBusiness] = useState({
    monthly_revenue: '',
    cash_flow: '',
    burn_rate: '',
    active_leads: '',
    mrr: '',
    active_clients: '',
    team_size: '',
    avg_deal_size: '',
    conversion_rate: '',
    top_challenge: '',
    current_focus: ''
  });
  const [sectorSpec, setSectorSpec] = useState({ sector_key: 'default', sector_label: '', metrics: [], diagnostic_questions: [] });
  const [sectorMetrics, setSectorMetrics] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profRes, specRes] = await Promise.all([
          api.get('/api/profile/full'),
          api.get('/api/sectors/spec'),
        ]);
        if (profRes.data.profile) setProfile(prev => ({ ...prev, ...profRes.data.profile }));
        if (profRes.data.business_data) {
          setBusiness(prev => ({ ...prev, ...profRes.data.business_data }));
          // Pre-fill sector_metrics if present
          if (profRes.data.business_data.sector_metrics) {
            const sm = profRes.data.business_data.sector_metrics;
            const stringified = Object.fromEntries(
              Object.entries(sm).map(([k, v]) => [k, v == null ? '' : String(v)])
            );
            setSectorMetrics(stringified);
          }
        }
        setSectorSpec(specRes.data || { sector_key: 'default', sector_label: '', metrics: [], diagnostic_questions: [] });
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessChange = (field, value) => {
    setBusiness(prev => ({ ...prev, [field]: value }));
  };

  const handleSectorMetricChange = (id, value) => {
    setSectorMetrics(prev => ({ ...prev, [id]: value }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const profileData = Object.fromEntries(
        Object.entries(profile).filter(([_, v]) => v !== '' && v !== null)
      );
      if (Object.keys(profileData).length > 0) {
        await api.put('/api/profile/details', profileData);
      }

      const businessData = {};
      Object.entries(business).forEach(([k, v]) => {
        if (v !== '' && v !== null) {
          if (['monthly_revenue', 'cash_flow', 'burn_rate', 'mrr', 'avg_deal_size', 'conversion_rate'].includes(k)) {
            businessData[k] = parseFloat(v) || null;
          } else if (['active_leads', 'active_clients', 'team_size', 'sales_cycle_days'].includes(k)) {
            businessData[k] = parseInt(v) || null;
          } else {
            businessData[k] = v;
          }
        }
      });

      // Sector metrics — parse numbers when type is numeric
      const sectorPayload = {};
      sectorSpec.metrics.forEach(m => {
        const raw = sectorMetrics[m.id];
        if (raw === undefined || raw === '' || raw === null) return;
        if (['number', 'percent', 'currency', 'days'].includes(m.type)) {
          const num = parseFloat(String(raw).replace(',', '.'));
          if (!isNaN(num)) sectorPayload[m.id] = num;
        } else {
          sectorPayload[m.id] = raw;
        }
      });
      if (Object.keys(sectorPayload).length > 0) {
        businessData.sector_metrics = sectorPayload;
      }

      if (Object.keys(businessData).length > 0) {
        await api.put('/api/business-data', businessData);
      }

      toast.success(t('business.dataSaved'));
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('business.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-atlasCyan animate-spin" />
      </div>
    );
  }

  const runway = business.cash_flow && business.burn_rate && parseFloat(business.burn_rate) > 0
    ? Math.round(parseFloat(business.cash_flow) / parseFloat(business.burn_rate) * 10) / 10
    : null;

  return (
    <div data-testid="business-page" className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('business.title')}</h1>
          <p className="text-atlasTextSecondary">{t('business.subtitle')}</p>
        </div>
        <Button 
          data-testid="save-all-btn"
          onClick={saveAll} 
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('business.saveAll')}
        </Button>
      </div>

      {/* Quick Stats Banner */}
      {runway && (
        <div className={`glass-card p-4 flex items-center gap-4 ${runway < 3 ? 'border-atlasRed/50' : runway < 6 ? 'border-atlasYellow/50' : 'border-green-500/50'}`}>
          <AlertTriangle className={`w-6 h-6 ${runway < 3 ? 'text-atlasRed' : runway < 6 ? 'text-atlasYellow' : 'text-green-500'}`} />
          <div>
            <p className="text-white font-medium">
              {t('business.runway')}: <span className={runway < 3 ? 'text-atlasRed' : runway < 6 ? 'text-atlasYellow' : 'text-green-500'}>{runway} {t('business.months')}</span>
            </p>
            <p className="text-atlasTextSecondary text-sm">
              {runway < 3 ? t('business.runwayStatus.critical') : runway < 6 ? t('business.runwayStatus.monitor') : t('business.runwayStatus.healthy')}
            </p>
          </div>
        </div>
      )}

      {/* CEO Profile Section */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-atlasCyan" />
          {t('business.ceoProfile')}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.companyName')}</Label>
            <Input
              data-testid="company-name-input"
              value={profile.company_name}
              onChange={(e) => handleProfileChange('company_name', e.target.value)}
              className="input-atlas mt-1"
              placeholder="Acme Inc."
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.industry')}</Label>
            <Input
              data-testid="industry-input"
              value={profile.industry}
              onChange={(e) => handleProfileChange('industry', e.target.value)}
              className="input-atlas mt-1"
              placeholder={t('business.fields.industryPlaceholder')}
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.companyStage')}</Label>
            <Select value={profile.company_stage} onValueChange={(v) => handleProfileChange('company_stage', v)}>
              <SelectTrigger data-testid="stage-select" className="input-atlas mt-1">
                <SelectValue placeholder={t('business.selectStage')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="idea">{t('business.fields.stages.idea')}</SelectItem>
                <SelectItem value="mvp">{t('business.fields.stages.mvp')}</SelectItem>
                <SelectItem value="growth">{t('business.fields.stages.growth')}</SelectItem>
                <SelectItem value="scale">{t('business.fields.stages.scale')}</SelectItem>
                <SelectItem value="mature">{t('business.fields.stages.mature')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.role')}</Label>
            <Input
              data-testid="role-input"
              value={profile.role}
              onChange={(e) => handleProfileChange('role', e.target.value)}
              className="input-atlas mt-1"
              placeholder={t('business.fields.rolePlaceholder')}
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.decisionStyle')}</Label>
            <Select value={profile.decision_style} onValueChange={(v) => handleProfileChange('decision_style', v)}>
              <SelectTrigger data-testid="style-select" className="input-atlas mt-1">
                <SelectValue placeholder={t('business.howDecide')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="analytical">{t('business.fields.styles.analytical')}</SelectItem>
                <SelectItem value="intuitive">{t('business.fields.styles.intuitive')}</SelectItem>
                <SelectItem value="collaborative">{t('business.fields.styles.collaborative')}</SelectItem>
                <SelectItem value="directive">{t('business.fields.styles.directive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.goalsQ1')}</Label>
            <Input
              data-testid="goals-q1-input"
              value={profile.goals_q1}
              onChange={(e) => handleProfileChange('goals_q1', e.target.value)}
              className="input-atlas mt-1"
              placeholder={t('business.fields.goalsQ1Placeholder')}
            />
          </div>
        </div>
      </div>

      {/* Business Metrics Section */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-atlasCyan" />
          {t('business.businessMetrics')}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> {t('business.fields.monthlyRevenue')}
            </Label>
            <Input
              data-testid="revenue-input"
              type="number"
              value={business.monthly_revenue}
              onChange={(e) => handleBusinessChange('monthly_revenue', e.target.value)}
              className="input-atlas mt-1"
              placeholder="50000"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> {t('business.fields.cashFlow')}
            </Label>
            <Input
              data-testid="cash-input"
              type="number"
              value={business.cash_flow}
              onChange={(e) => handleBusinessChange('cash_flow', e.target.value)}
              className="input-atlas mt-1"
              placeholder="150000"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> {t('business.fields.burnRate')}
            </Label>
            <Input
              data-testid="burn-input"
              type="number"
              value={business.burn_rate}
              onChange={(e) => handleBusinessChange('burn_rate', e.target.value)}
              className="input-atlas mt-1"
              placeholder="25000"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> {t('business.fields.mrr')}
            </Label>
            <Input
              data-testid="mrr-input"
              type="number"
              value={business.mrr}
              onChange={(e) => handleBusinessChange('mrr', e.target.value)}
              className="input-atlas mt-1"
              placeholder="45000"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <Target className="w-4 h-4" /> {t('business.fields.activeLeads')}
            </Label>
            <Input
              data-testid="leads-input"
              type="number"
              value={business.active_leads}
              onChange={(e) => handleBusinessChange('active_leads', e.target.value)}
              className="input-atlas mt-1"
              placeholder="25"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <Users className="w-4 h-4" /> {t('business.fields.activeClients')}
            </Label>
            <Input
              data-testid="clients-input"
              type="number"
              value={business.active_clients}
              onChange={(e) => handleBusinessChange('active_clients', e.target.value)}
              className="input-atlas mt-1"
              placeholder="120"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <Users className="w-4 h-4" /> {t('business.fields.teamSize')}
            </Label>
            <Input
              data-testid="team-input"
              type="number"
              value={business.team_size}
              onChange={(e) => handleBusinessChange('team_size', e.target.value)}
              className="input-atlas mt-1"
              placeholder="8"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> {t('business.fields.avgDealSize')}
            </Label>
            <Input
              data-testid="deal-input"
              type="number"
              value={business.avg_deal_size}
              onChange={(e) => handleBusinessChange('avg_deal_size', e.target.value)}
              className="input-atlas mt-1"
              placeholder="5000"
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.conversionRate')}</Label>
            <Input
              data-testid="conversion-input"
              type="number"
              value={business.conversion_rate}
              onChange={(e) => handleBusinessChange('conversion_rate', e.target.value)}
              className="input-atlas mt-1"
              placeholder="15"
            />
          </div>
        </div>
      </div>

      {/* Sector empty-state CTA — visible only when no sector is set */}
      {(!sectorSpec.metrics || sectorSpec.metrics.length === 0) && sectorSpec.sector_key === 'default' && (
        <div className="glass-card p-5 border border-atlasCyan/20 flex items-start gap-3" data-testid="sector-empty-state">
          <Compass className="w-5 h-5 text-atlasCyan flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-white font-medium mb-1">Active l'expertise sectorielle</p>
            <p className="text-atlasTextSecondary text-sm leading-relaxed">
              Renseigne ton secteur d'activité dans <span className="text-atlasCyan">Profil → Modifier</span> pour
              débloquer les KPIs et benchmarks spécifiques à ton métier.
            </p>
          </div>
        </div>
      )}

      {/* Sector-specific KPIs (Dashboard Biz adaptatif) */}
      {sectorSpec.metrics && sectorSpec.metrics.length > 0 && (
        <div className="glass-card p-6" data-testid="sector-metrics-card">
          <div className="flex items-start justify-between mb-2 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Compass className="w-5 h-5 text-atlasCyan flex-shrink-0" />
              <h2 className="text-xl font-semibold text-white truncate">
                KPIs métier — {sectorSpec.sector_label}
              </h2>
            </div>
            <span className="text-[10px] px-2 py-1 rounded-full bg-atlasCyan/15 text-atlasCyan font-medium tracking-wide whitespace-nowrap">
              EXPERTISE ACTIVÉE
            </span>
          </div>
          <p className="text-atlasTextSecondary text-sm mb-6 leading-relaxed">
            Renseigne ces indicateurs : ATLAS les compare aux benchmarks de ton secteur et adapte ses recommandations.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {sectorSpec.metrics.map((m) => (
              <div key={m.id} data-testid={`sector-metric-${m.id}`}>
                <Label className="text-atlasTextSecondary flex items-center gap-1 text-sm">
                  {m.label}
                  {m.unit && <span className="text-atlasTextSecondary/70 text-xs ml-1">({m.unit})</span>}
                </Label>
                <Input
                  data-testid={`sector-input-${m.id}`}
                  type={['number', 'percent', 'currency', 'days'].includes(m.type) ? 'number' : 'text'}
                  step={m.type === 'percent' || m.type === 'number' ? '0.1' : undefined}
                  value={sectorMetrics[m.id] ?? ''}
                  onChange={(e) => handleSectorMetricChange(m.id, e.target.value)}
                  className="input-atlas mt-1"
                  placeholder={m.placeholder}
                />
                {(m.hint || m.benchmark) && (
                  <p className="text-[11px] text-atlasTextSecondary/80 mt-1.5 leading-snug">
                    {m.hint}
                    {m.hint && m.benchmark ? ' · ' : ''}
                    {m.benchmark && <span className="text-atlasCyan/80">cible {m.benchmark}</span>}
                  </p>
                )}
              </div>
            ))}
          </div>

          {sectorSpec.diagnostic_questions && sectorSpec.diagnostic_questions.length > 0 && (
            <div className="mt-6 p-4 rounded-lg border border-atlasCyan/15 bg-atlasCyan/5">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-atlasCyan" />
                <p className="text-white text-sm font-medium">Questions stratégiques qu'ATLAS te poserait</p>
              </div>
              <ul className="space-y-1.5">
                {sectorSpec.diagnostic_questions.map((q, i) => (
                  <li key={i} className="text-xs text-atlasTextSecondary leading-relaxed pl-4 relative">
                    <span className="absolute left-0 text-atlasCyan">→</span> {q}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Focus Section */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Target className="w-5 h-5 text-atlasCyan" />
          {t('business.currentFocus')}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.topChallenge')}</Label>
            <Textarea
              data-testid="challenge-input"
              value={business.top_challenge}
              onChange={(e) => handleBusinessChange('top_challenge', e.target.value)}
              className="input-atlas mt-1"
              placeholder={t('business.fields.topChallengePlaceholder')}
              rows={3}
            />
          </div>
          <div>
            <Label className="text-atlasTextSecondary">{t('business.fields.currentFocus')}</Label>
            <Textarea
              data-testid="focus-input"
              value={business.current_focus}
              onChange={(e) => handleBusinessChange('current_focus', e.target.value)}
              className="input-atlas mt-1"
              placeholder={t('business.fields.currentFocusPlaceholder')}
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-atlasCyan/10 border border-atlasCyan/20">
        <CheckCircle2 className="w-5 h-5 text-atlasCyan flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white font-medium">{t('business.infoNote.title')}</p>
          <p className="text-atlasTextSecondary text-sm">
            {t('business.infoNote.description')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Business;
